import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsMobileMenuOpen(false);
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <button 
            onClick={() => handleNavigation('/')}
            className="flex items-center gap-3 cursor-pointer"
          >
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              isScrolled ? 'bg-gradient-to-br from-orange-500 to-orange-600' : 'bg-white/20 backdrop-blur-sm'
            }`}>
              <i className="ri-window-line text-2xl text-white"></i>
            </div>
            <div>
              <div className={`text-xl font-bold ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
                AccèsLeads
              </div>
              <div className={`text-xs ${isScrolled ? 'text-gray-600' : 'text-white/80'}`}>
                Demandes Fenêtres
              </div>
            </div>
          </button>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-8">
            <button 
              onClick={() => scrollToSection('services')}
              className={`font-medium transition-colors cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-orange-600' : 'text-white hover:text-orange-300'
              }`}
            >
              Services
            </button>
            <button 
              onClick={() => scrollToSection('whatsapp-alerts')}
              className={`font-medium transition-colors cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-orange-600' : 'text-white hover:text-orange-300'
              }`}
            >
              Alertes WhatsApp
            </button>
            <button 
              onClick={() => handleNavigation('/expertises')}
              className={`font-medium transition-colors cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-orange-600' : 'text-white hover:text-orange-300'
              }`}
            >
              Expertises
            </button>
            <button 
              onClick={() => handleNavigation('/conseils')}
              className={`font-medium transition-colors cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-orange-600' : 'text-white hover:text-orange-300'
              }`}
            >
              Conseils
            </button>
            <button 
              onClick={() => scrollToSection('faq')}
              className={`font-medium transition-colors cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-orange-600' : 'text-white hover:text-orange-300'
              }`}
            >
              FAQ
            </button>
            <button 
              onClick={() => scrollToSection('contact')}
              className={`font-medium transition-colors cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-orange-600' : 'text-white hover:text-orange-300'
              }`}
            >
              Contact
            </button>
            <button 
              onClick={() => handleNavigation('/inscription-candidat')}
              className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-orange-600 hover:to-orange-700 transition-all whitespace-nowrap cursor-pointer"
            >
              Inscription
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg cursor-pointer ${
              isScrolled ? 'bg-gray-100' : 'bg-white/20 backdrop-blur-sm'
            }`}
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl ${
              isScrolled ? 'text-gray-900' : 'text-white'
            }`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200 shadow-lg">
          <div className="px-4 py-6 space-y-4">
            <button 
              onClick={() => scrollToSection('services')}
              className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg font-medium cursor-pointer"
            >
              Services
            </button>
            <button 
              onClick={() => scrollToSection('whatsapp-alerts')}
              className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg font-medium cursor-pointer"
            >
              Alertes WhatsApp
            </button>
            <button 
              onClick={() => handleNavigation('/expertises')}
              className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg font-medium cursor-pointer"
            >
              Expertises
            </button>
            <button 
              onClick={() => handleNavigation('/conseils')}
              className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg font-medium cursor-pointer"
            >
              Conseils
            </button>
            <button 
              onClick={() => scrollToSection('faq')}
              className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg font-medium cursor-pointer"
            >
              FAQ
            </button>
            <button 
              onClick={() => scrollToSection('contact')}
              className="block w-full text-left px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg font-medium cursor-pointer"
            >
              Contact
            </button>
            <button 
              onClick={() => handleNavigation('/inscription-candidat')}
              className="block w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-orange-600 hover:to-orange-700 transition-all text-center cursor-pointer"
            >
              Inscription
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}